import React from 'react';
import { T, sx } from '../lib/tokens';
import { Wordmark } from '../components/ui/Wordmark';
import { useIsMobile } from '../lib/useIsMobile';

export function Privacy() {
  const m = useIsMobile();
  return (
    <LegalShell title="Privacy policy" subtitle="Last updated 18 April 2026" m={m}>
      <p style={p}>
        We run receiptrec so small businesses can get receipts into Xero without Sunday-night
        data entry. This page explains what we collect to make that happen, and what we do
        (and don't do) with it.
      </p>

      <h2 style={h2}>What we collect</h2>
      <p style={p}>
        <strong>Xero OAuth tokens</strong> to post transactions on your behalf, and
        <strong> Gmail OAuth tokens</strong> if you choose to connect Gmail so we can find
        receipts in your inbox. Both are encrypted with AES-256-GCM before they hit our database.
      </p>
      <p style={p}>
        We store the <strong>receipt files</strong> you send us - images, PDFs, HTML email
        bodies - in Cloudflare R2 until the transaction is posted and reconciled. We also keep
        <strong> email metadata</strong> (sender and subject) from forwarded receipts, plus
        <strong> usage counters</strong> (how many receipts you've processed) and
        <strong> billing records</strong> (Stripe customer id, subscription status).
      </p>

      <h2 style={h2}>How we use it</h2>
      <p style={p}>
        We parse each receipt with Anthropic's Claude models to pull out vendor, date,
        amount, tax and account. We create a Spend Money transaction in your Xero tenant
        and attach the original file. We send you notification emails when something needs
        your review. We invoice you via Stripe.
      </p>

      <h2 style={h2}>Who we share with</h2>
      <p style={p}>We use a small number of subprocessors to run the service:</p>
      <ul style={ul}>
        <li style={li}><strong>Cloudflare</strong> - hosting, D1 database, R2 object storage</li>
        <li style={li}><strong>Anthropic</strong> - receipt parsing (zero-retention API)</li>
        <li style={li}><strong>Stripe</strong> - payments and subscription billing</li>
        <li style={li}><strong>Resend</strong> - outbound notification email</li>
        <li style={li}><strong>Google</strong> - Gmail API, only if you connect a Gmail account</li>
      </ul>

      <h2 style={h2}>Retention</h2>
      <p style={p}>
        We keep your data while your account is active. If you delete your account, we delete
        your receipts, files, tokens and user record within 30 days. Audit logs are retained
        for 12 months for security and billing dispute reasons.
      </p>

      <h2 style={h2}>Your rights under the Australian Privacy Principles</h2>
      <p style={p}>
        You can ask to <strong>access</strong> the personal information we hold about you,
        request <strong>correction</strong> of anything inaccurate, or ask us to
        <strong> delete</strong> your account and data. Email privacy@receiptrec.com and
        we'll respond within 30 days. If you're not satisfied with our response, you can
        complain to the Office of the Australian Information Commissioner (OAIC).
      </p>

      <h2 style={h2}>International transfers</h2>
      <p style={p}>
        Some of our subprocessors (Cloudflare, Anthropic, Stripe, Resend, Google) operate in
        the United States. Your data is protected in transit with TLS 1.3, and where data is
        transferred internationally we rely on Standard Contractual Clauses.
      </p>

      <h2 style={h2}>Cookies</h2>
      <p style={p}>
        We only set HttpOnly session cookies required to keep you signed in. No tracking
        cookies, no analytics cookies, no third-party pixels.
      </p>

      <h2 style={h2}>Changes to this policy</h2>
      <p style={p}>
        If we make a material change, we'll email every active user at least 30 days before
        it takes effect.
      </p>

      <h2 style={h2}>Contact</h2>
      <p style={p}>
        Questions about privacy? Email <a style={a} href="mailto:privacy@receiptrec.com">privacy@receiptrec.com</a>.
      </p>
    </LegalShell>
  );
}

// ---- shared styles ----
const p: React.CSSProperties = { fontSize: 16, lineHeight: 1.6, color: T.graphite, margin: '0 0 18px' };
const h2: React.CSSProperties = { ...sx.serif, fontSize: 26, letterSpacing: '-0.015em', fontWeight: 400, margin: '36px 0 14px', color: T.graphite };
const ul: React.CSSProperties = { margin: '0 0 18px', paddingLeft: 22 };
const li: React.CSSProperties = { fontSize: 16, lineHeight: 1.6, color: T.graphite, margin: '4px 0' };
const a: React.CSSProperties = { color: T.sageDarker, textDecoration: 'underline' };

function LegalShell({ title, subtitle, m, children }: { title: string; subtitle?: string; m: boolean; children: React.ReactNode }) {
  return (
    <div style={{ background: T.paper, color: T.graphite, minHeight: '100vh' }}>
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: T.navy,
          borderBottom: `1px solid ${T.navyLine}`,
        }}
      >
        <div
          style={{
            maxWidth: 1240,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: m ? '14px 20px' : '18px 40px',
          }}
        >
          <a href="#/" style={{ textDecoration: 'none' }}>
            <Wordmark size={m ? 20 : 24} />
          </a>
          <a
            href="#/"
            style={{
              fontSize: 14,
              color: T.creamMuted,
              textDecoration: 'none',
            }}
          >
            Back to home
          </a>
        </div>
      </header>
      <main
        style={{
          maxWidth: 720,
          margin: '0 auto',
          padding: m ? '32px 20px' : '60px 40px',
        }}
      >
        <h1
          style={{
            ...sx.serif,
            fontSize: m ? 32 : 48,
            lineHeight: 1.05,
            letterSpacing: '-0.03em',
            fontWeight: 400,
            margin: '0 0 10px',
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <div style={{ fontSize: 13, color: T.graphiteDim, marginBottom: 28 }}>{subtitle}</div>
        )}
        {children}
      </main>
    </div>
  );
}
